import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
import { categories } from '../data/products';
import { getRegionMeta } from '../utils/region';
export function Contact() {
  const region = getRegionMeta();
  const addressPlaceholder = `123 Main St, ${region.cities[0]}, WA`;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const form = e.target as HTMLFormElement;
      const materialSelect = form.querySelector('select[class*="appearance-none"]') as HTMLSelectElement;
      // Estimated Yardage is now a free-text input (was previously a
      // restrictive dropdown with a wrong "5 yd minimum"). Read it by name.
      const yardageInput = form.querySelector('input[name="estimatedYardage"]') as HTMLInputElement | null;
      const addressInput = form.querySelector('input[name="deliveryAddress"]') as HTMLInputElement;
      const rc = region.code;
      const brandName = `${rc} Gravel`;
      const customerName = formData.name.trim();
      // Greet by first name only — "Hi John Smith!" reads like a form letter.
      const firstName = customerName.split(/\s+/)[0] || 'there';
      // The EmailJS template renders {{message}} as the email body — keys
      // containing spaces (e.g. "Delivery Address") are not valid EmailJS
      // variables and never render, which is why these arrived blank. Compose
      // the full body here, exactly as the order flow does.
      const dispatchBody = ['New contact / quote request', '', `Name: ${customerName || '—'}`, `Email: ${formData.email || '—'}`, `Phone: ${formData.phone || '—'}`, `Delivery address: ${addressInput?.value || '—'}`, `Material: ${materialSelect?.value || '—'}`, `Estimated yardage: ${yardageInput?.value || '—'}`, '', 'Message:', formData.message || '—'].join('\n');
      await sendFormEmail({
        subject: `${rc}Gravel — New Contact / Quote Request`,
        to_name: customerName,
        from_name: brandName,
        message: dispatchBody,
        Name: customerName,
        email: formData.email,
        _replyto: formData.email,
        _autoresponse: `Hi ${firstName}! Thank you for reaching out to ${brandName}! We've received your message and will get back to you shortly.\n\nIf you need anything sooner, call us at (206) 650-0129.\n\n- The ${brandName} Team\n\n----------\n\n206Gravel LLC · Bonney Lake, WA · info@206gravel.com`,
        Phone: formData.phone,
        Material: materialSelect?.value || '',
        'Estimated Yardage': yardageInput?.value || '',
        'Delivery Address': addressInput?.value || '',
        Message: formData.message
      });
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      setTimeout(() => {
        formRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Dark mode keeps the deeper, stepped-down palette; light
  // mode reverts to the original warm-cream tones so the page
  // reads bright and friendly in daylight while still feeling
  // grounded after dark.
  const inputClass = 'w-full bg-[#f2efe8] dark:bg-[#2d2c2b] border border-stone-300 dark:border-[#3e3d3d] rounded-xl px-4 py-3 text-sm text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent outline-none transition-all';
  return <main className="min-h-[calc(100vh-64px)] bg-[#F2EFE8] dark:bg-[#2d2c2b] py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 flex items-center justify-center" data-id="element-2865" data-mp="208r2cm7zrmzg">
      <div className="max-w-6xl w-full mx-auto" data-id="element-2866" data-mp="2sx54ne6455gt">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="mb-10 text-center lg:text-left" data-id="element-2867" data-mp="2nxseajg19g9h">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-white mb-3 tracking-tight" data-id="element-2868" data-mp="2exna0i64yxzj">
            Contact & <span className="text-brand-orange" data-id="element-2869" data-mp="17l3mfkho1kj5">Quotes</span>
          </h1>
          <p className="text-stone-600 dark:text-stone-300 text-sm md:text-base max-w-2xl mx-auto lg:mx-0 font-sub" data-id="element-2870" data-mp="13gvuzfjtpqh8">
            Need a quote or have a question? Reach out directly or drop your
            project details below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2871" data-mp="3o5yxrsilk5g7">
          {/* Left Column: Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} ref={formRef} className="lg:col-span-2 bg-[#fcfbfa] dark:bg-[#3a3939] rounded-2xl p-6 md:p-8 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2872" data-mp="3swp3x7yukcby">
            {isSubmitted ? <div className="h-full flex flex-col items-center justify-center text-center py-12" data-id="element-2873" data-mp="arlqmzyq8u0g">
                <div className="w-16 h-16 bg-brand-orange/10 rounded-full flex items-center justify-center mb-4" data-id="element-2874" data-mp="fpnbnqfpr3ek">
                  <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-2875" data-mp="2ckjqvguu2ovf" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-2" data-id="element-2876" data-mp="2hhs9fijw8ux7">
                  Request Received
                </h3>
                <p className="text-stone-600 dark:text-stone-300 mb-6 max-w-sm text-sm" data-id="element-2877" data-mp="3fu4m1mq3sl6m">
                  Thanks for reaching out. We'll get back to you shortly with
                  pricing and availability.
                </p>
                <button onClick={() => setIsSubmitted(false)} className="px-6 py-2.5 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-none font-bold text-sm transition-all" data-id="element-2878" data-mp="3osholh45oerz">
                  Send Another Request
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2879" data-mp="ger4c41klso3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2880" data-mp="gg2agaihncid">
                  <div data-id="element-2881" data-mp="cd5xazbpw8lt">
                    <label htmlFor="contact-name" className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2882" data-mp="cf083kt9ykc6">
                      Name *
                    </label>
                    <input id="contact-name" type="text" name="name" autoComplete="name" value={formData.name} onChange={handleChange} required className={inputClass} data-id="element-2883" data-mp="lb7v7605ml4v" />
                  </div>
                  <div data-id="element-2884" data-mp="19xfi5y9iz282">
                    <label htmlFor="contact-email" className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2885" data-mp="v0y95dot5zoo">
                      Email Address *
                    </label>
                    <input id="contact-email" type="email" name="email" autoComplete="email" value={formData.email} onChange={handleChange} required className={inputClass} data-id="element-2886" data-mp="m8mm81swkudg" />
                  </div>
                </div>

                <div data-id="element-2887" data-mp="caynl0s6f2q0">
                  <label htmlFor="contact-phone" className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2888" data-mp="cbro3k0jkvzp">
                    Phone Number
                  </label>
                  <input id="contact-phone" type="tel" name="phone" autoComplete="tel" value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-2889" data-mp="3athctypyadf" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2890" data-mp="nrefgsv45q3s">
                  <div data-id="element-2891" data-mp="noigd17j4vdr">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2892" data-mp="ir8n1ah0rzkn">
                      Material Needed
                    </label>
                    <select className={inputClass + ' appearance-none'} data-id="element-2893" data-mp="8tm4me0ihpvx">
                      <option value="" data-id="element-2894" data-mp="8tc8s9sscoib">Select a material...</option>
                      {categories.map(cat => <optgroup key={cat.name} label={cat.name} data-id="element-2895" data-mp="4tbm61vj9706">
                          {cat.products.map(p => <option key={p.slug} value={p.name} data-id="element-2896" data-mp="3w53l25t4p2b2">
                              {p.name} — ${p.pricePerYard}/yd
                            </option>)}
                        </optgroup>)}
                      <option value="Not Sure Yet" data-id="element-2897" data-mp="3ma1vx9yus08h">Not Sure Yet</option>
                    </select>
                  </div>
                  <div data-id="element-2898" data-mp="3hbi62yh524au">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2899" data-mp="3d82cyc4a2m2w">
                      Estimated Yardage
                    </label>
                    <input type="text" name="estimatedYardage" className={inputClass} placeholder='e.g. "12 yards", "3 truckloads", "not sure"' data-id="element-2900" data-mp="1ss49sr4zcwbm" />
                  </div>
                </div>

                <div data-id="element-2901" data-mp="21od985syjuxr">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2902" data-mp="26pj161esne68">
                    Delivery Address *
                  </label>
                  <input type="text" name="deliveryAddress" required className={inputClass} placeholder={addressPlaceholder} data-id="element-2903" data-mp="18zpjny16ejee" />
                </div>

                <div data-id="element-2904" data-mp="1izorfspru5q7">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2905" data-mp="1iz6pcfgfgxad">
                    Message *
                  </label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className={inputClass + ' resize-none'} data-id="element-2906" data-mp="qbsxgvrl6rx4"></textarea>
                </div>

                <div className="pt-2" data-id="element-2907" data-mp="oxm1nduvw2tq">
                  <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 bg-brand-button hover:bg-brand-buttonHover text-white rounded-none font-bold text-sm transition-all group disabled:opacity-60" data-id="element-2908" data-mp="owkguho2g1yt">
                    {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2909" data-mp="13v4qshbh4i1p" /> : <>
                        Send Quote Request
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2910" data-mp="b6784d3coj8f" />
                      </>}
                  </button>
                </div>
              </form>}
          </motion.div>

          {/* Right Column: Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="space-y-6" data-id="element-2911" data-mp="kxjx8wxrkvar">
            {/* Contact Info Card */}
            <div className="bg-[#fcfbfa] dark:bg-[#3a3939] text-stone-900 dark:text-white rounded-2xl p-6 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2912" data-mp="25ppigkynddv4">
              <h3 className="font-heading text-lg font-bold mb-5" data-id="element-2913" data-mp="21m9pbylsdvn6">
                Get in Touch
              </h3>
              <div className="space-y-4" data-id="element-2914" data-mp="1wr4657jrqv2s">
                <a href="tel:206-650-0129" className="flex items-center group" data-id="element-2915" data-mp="1rp50fr0k3m2o">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-2916" data-mp="1mrn20uk60p8x">
                    <Phone className="w-4 h-4 text-brand-orange" data-id="element-2917" data-mp="1hyaeknr82ohr" />
                  </div>
                  <div data-id="element-2918" data-mp="1dz1njqnkh4pw">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2919" data-mp="18zp0b4ss9vms">
                      Call Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2920" data-mp="13zkhh84ekote">
                      (206) 650-0129
                    </p>
                  </div>
                </a>
                <a href="mailto:info@206gravel.com" className="flex items-center group" data-id="element-2921" data-mp="pdnd6fzu67cr">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-2922" data-mp="udj9ru67ewia">
                    <Mail className="w-4 h-4 text-brand-orange" data-id="element-2923" data-mp="gfl6pkxc2mi3" />
                  </div>
                  <div data-id="element-2924" data-mp="kgwox5dvo5g9">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2925" data-mp="184enpkvk83sq">
                      Email Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2926" data-mp="1d26bc2p693r4">
                      info@206gravel.com
                    </p>
                  </div>
                </a>
                <div className="flex items-center" data-id="element-2927" data-mp="13cmsuy5dfbws">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3" data-id="element-2928" data-mp="13bbrt63b8i9d">
                    <MapPin className="w-4 h-4 text-brand-orange" data-id="element-2929" data-mp="1vtqt09nrnrpr" />
                  </div>
                  <div data-id="element-2930" data-mp="3gkeaa5163d2m">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2931" data-mp="3gjmfuxjb3ldx">
                      Location
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2932" data-mp="3bl1egusbivnw">
                      Bonney Lake, WA
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-[#fcfbfa] dark:bg-[#3a3939] rounded-2xl p-6 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2933" data-mp="2xml7d2qjn358">
              <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-4 flex items-center" data-id="element-2934" data-mp="2so9x4qwc6qmh">
                <Clock className="w-4 h-4 mr-2 text-brand-orange" data-id="element-2935" data-mp="37hd6iazrxy24" />
                Hours
              </h3>
              <div className="space-y-3 text-sm" data-id="element-2936" data-mp="mth09it8v6k2">
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#4a4949]" data-id="element-2937" data-mp="ms5vbfo2bsrd">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2938" data-mp="hu4b2rewhneb">
                    Mon - Fri
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-2939" data-mp="2shriatij4gz1">
                    6:00 AM – 5:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#4a4949]" data-id="element-2940" data-mp="2wi1kdv37an8m">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2941" data-mp="31g34mjcd4slo">
                    Saturday
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-2942" data-mp="3bg3pwatslisi">
                    7:00 AM – 3:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-2943" data-mp="3g9gbsd737bhk">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2944" data-mp="3k7nl4mz81aiq">
                    Sunday
                  </span>
                  <span className="font-medium text-brand-orange" data-id="element-2945" data-mp="3p5xf4u97m52r">Closed</span>
                </div>
              </div>
            </div>

            {/* Service Area */}
            <div className="px-1 pt-2" data-id="element-2946" data-mp="86gswbfhchs2">
              <h4 className="font-heading text-sm font-bold text-stone-900 dark:text-white mb-2" data-id="element-2947" data-mp="83b3sw6upfwc">
                Service Area
              </h4>
              <p className="text-xs text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2948" data-mp="3udx5oq3r71rw">
                Proudly serving King and Pierce counties — Seattle, Bellevue,
                Kirkland, Redmond, Renton, Kent, Auburn, Federal Way, Tacoma,
                Puyallup, Lakewood, Bonney Lake, and beyond — with reliable,
                same-day and next-day delivery. Not sure if you're in our
                delivery zone? Give us a call — we're happy to help.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </main>;
}